<template>
	<div>
		<v-dialog
			scrollable
			transition="dialog-bottom-transition"
			:value="isVisible"
			max-width="800"
			persistent
			@click:outside="handleClickOutside"
			@keydown.esc="handleClickOutside"
		>
			<v-card>
				<v-card-title style="display: block; padding: 0; font-size: 1rem">
					<v-stepper alt-labels v-model="stepper" flat>
						<v-stepper-header>
							<v-stepper-step :step="1">
								{{ $t('label.requisition-form') }}
							</v-stepper-step>

							<v-divider></v-divider>

							<v-stepper-step :step="2">
								{{ $t('label.approval-flow') }}
							</v-stepper-step>

							<v-divider></v-divider>

							<v-stepper-step :step="3">
								{{ $t('label.summary') }}
							</v-stepper-step>
						</v-stepper-header>
					</v-stepper>
				</v-card-title>

				<v-divider></v-divider>

				<v-card-text style="padding: 0">
					<v-stepper v-model="stepper" flat>
						<v-stepper-items>
							<v-stepper-content :step="1">
								<ValidationObserver
									ref="form_step_1"
									tag="form"
									@submit="handleClickNext"
									id="form_step_1"
								>
									<div v-if="requisitionType === 'new_job'">
										<PositionPicker
											:departmentId="job.department_id"
											:teamId="job.team_id"
											:positionId="
												requisitionFormTemplate.predefined_position_id
											"
											:positionTitle="job.name"
											@change="handlePositionPickerChange"
										></PositionPicker>

										<v-container>
											<v-row v-if="similarPositionRequisitions.length > 0">
												<v-col>
													<fieldset
														class="talentics-fieldset talentics-fieldset-paddings"
													>
														<legend>
															{{
																$t(
																	'label.autofill-from-similar-position-requisitions',
																)
															}}
														</legend>
														<v-radio-group
															v-model="similarPositionRequisitionsRadioGroup"
															dense
															hide-details
															style="margin-top: 0; padding-top: 0"
															@change="
																handleSimilarPositionRequisitionsRadioGroupChange
															"
														>
															<v-radio :value="0">
																<template v-slot:label>
																	<div>
																		{{ $t('text.none') }}
																	</div>
																</template>
															</v-radio>
															<v-radio
																v-for="(
																	similarPositionRequisition, index
																) in similarPositionRequisitions"
																:key="`similarPositionRequisition_${index}`"
																:value="similarPositionRequisition.id"
															>
																<template v-slot:label>
																	<div>
																		{{ job.name }}
																		-
																		{{
																			handleGetUserName({
																				userId:
																					similarPositionRequisition.requested_by,
																			})
																		}}
																		-
																		{{
																			formatDate({
																				timestamp:
																					similarPositionRequisition.updated,
																			})
																		}}
																	</div>
																</template>
															</v-radio>
														</v-radio-group>
													</fieldset>
												</v-col>
											</v-row>

											<!-- USER PICKER -->
											<v-row>
												<v-col>
													<UserPicker
														v-model="job.owner"
														:users="users.filter(item => item.active)"
														:label="$t('label.owner') + '*'"
														dense
														rules="required"
													></UserPicker>
												</v-col>
											</v-row>

											<v-row>
												<v-col>
													<UserPicker
														v-model="job.hiring_manager"
														:users="users.filter(item => item.active)"
														:label="$t('label.hiring-manager') + '*'"
														rules="required"
														dense
													></UserPicker>
												</v-col>
											</v-row>

											<v-row>
												<v-col>
													<ValidationProvider
														rules="required"
														v-slot="{ errors }"
													>
														<fieldset
															class="talentics-fieldset talentics-fieldset-paddings"
														>
															<legend>{{ $t('label.job-type') }}*</legend>
															<v-radio-group
																v-model="requisitionFormTemplate.job_type"
																dense
																hide-details
																style="margin-top: 0; padding-top: 0"
																:error-messages="errors"
															>
																<v-radio value="new_position">
																	<template v-slot:label>
																		<div>
																			{{ $t('label.new-position') }}
																		</div>
																	</template>
																</v-radio>
																<v-radio value="replacement">
																	<template v-slot:label>
																		<div>
																			{{ $t('label.replacement') }}
																		</div>
																	</template>
																</v-radio>
															</v-radio-group>
														</fieldset>
													</ValidationProvider>
												</v-col>
											</v-row>

											<v-row>
												<v-col>
													<ValidationProvider
														rules="required"
														v-slot="{ errors }"
													>
														<v-text-field
															v-model.number="requisitionFormTemplate.number_of_people"
															:label="`${$t(
																'label.targeted-number-of-people-to-be-hired',
															)}*`"
															type="number"
															:min="1"
															hide-details
															dense
															outlined
															:error-messages="errors"
														></v-text-field>
													</ValidationProvider>
												</v-col>
											</v-row>

											<v-row>
												<v-col>
													<DatePicker
														v-model="requisitionFormTemplate.deadline"
														:label="$t('label.requisition-target-close-date')"
														dense
														clearable
													></DatePicker>
												</v-col>
											</v-row>

											<v-row>
												<v-col>
													<ValidationProvider
														rules="required"
														v-slot="{ errors }"
													>
														<v-autocomplete
															v-model="job.location"
															:items="
																locations?.map(item => ({
																	value: item.id,
																	text: item.name,
																	disabled: !item.active,
																}))
															"
															item-text="text"
															item-value="value"
															item-disabled="disabled"
															hide-no-data
															outlined
															:label="`${$t('label.location')}*`"
															:error-messages="errors"
															hide-details
															dense
														></v-autocomplete>
													</ValidationProvider>
												</v-col>
											</v-row>

											<v-row>
												<v-col>
													<ValidationProvider
														rules="required"
														v-slot="{ errors }"
													>
														<v-autocomplete
															v-model="job.work_type"
															:items="
																workTypes.map(item => ({
																	value: item.id,
																	text: item.name,
																	disabled: !item.active,
																}))
															"
															item-text="text"
															item-value="value"
															item-disabled="disabled"
															hide-no-data
															outlined
															:label="`${$t('label.work-type')}*`"
															:error-messages="errors"
															hide-details
															dense
														></v-autocomplete>
													</ValidationProvider>
												</v-col>
											</v-row>

											<v-row
												v-if="
													actionType === 'new' &&
													similarPositionRequisitionsRadioGroup === 0
												"
											>
												<v-col>
													<ValidationProvider
														rules="required"
														v-slot="{ errors }"
													>
														<v-select
															v-model="requisitionFormTemplateId"
															:items="
																requisitionFormTemplates.filter(
																	item => item.active,
																)
															"
															item-text="label"
															item-value="id"
															dense
															outlined
															hide-details
															:label="$t('label.requisition-form')"
															@change="
																newVal =>
																	handleGetRequisitionFormTemplate({
																		id: newVal,
																	})
															"
															:error-messages="errors"
														></v-select>
													</ValidationProvider>
												</v-col>
											</v-row>

											<v-row v-if="requisitionFormTemplate.initialized">
												<v-col>
													<DynamicForm
														:jsonSchema="requisitionFormTemplate.json_schema"
														:contextSchema="
															requisitionFormTemplate.context_schema
														"
														:onChange="handleDynamicFormChange"
													></DynamicForm>
												</v-col>
											</v-row>
										</v-container>
									</div>

									<div v-if="requisitionType === 'new_offer'">
										<v-container>
											<v-row v-if="actionType === 'new'">
												<v-col>
													<ValidationProvider
														rules="required"
														v-slot="{ errors }"
													>
														<v-select
															v-model="offerTemplateId"
															:items="offerTemplates"
															item-text="label"
															item-value="id"
															dense
															outlined
															hide-details
															:label="$t('label.requisition-form')"
															@change="
																newVal =>
																	handleGetOfferTemplate({
																		id: newVal,
																	})
															"
															:error-messages="errors"
														></v-select>
													</ValidationProvider>
												</v-col>
											</v-row>
										</v-container>

										<div v-if="offerTemplateId">
											<v-container>
												<v-row>
													<v-col>
														<fieldset
															class="talentics-fieldset talentics-fieldset-paddings"
														>
															<legend>
																{{ $t('label.offer-letter') }}
															</legend>
															<RichEditor
																v-model="offerTemplate.offer_template"
																refName="offerTemplate"
															></RichEditor>
														</fieldset>
													</v-col>
												</v-row>

												<v-row>
													<v-col>
														<fieldset
															class="talentics-fieldset talentics-fieldset-paddings"
														>
															<legend>
																{{ $t('label.email-settings') }}
															</legend>
															<v-container fluid>
																<v-row>
																	<v-col>
																		<fieldset
																			class="talentics-fieldset talentics-fieldset-paddings"
																		>
																			<legend>
																				{{ $t('label.auto-email') }}
																			</legend>
																			<v-switch
																				v-model="
																					offerTemplate.send_automated_email
																				"
																				color="success"
																				:label="
																					$t(
																						'label.send-auto-email-to-candidate',
																					)
																				"
																				inset
																				hide-details
																				dense
																				class="v-input--template-switch"
																			></v-switch>
																		</fieldset>
																	</v-col>
																</v-row>
															</v-container>
															<div v-if="offerTemplate.send_automated_email">
																<ScheduleEmailForm
																	:emailForm="{
																		send_time: 'afc',
																		template: offerTemplate.email_body,
																		subject: offerTemplate.email_subject,
																		sent_from: offerTemplate.sent_from,
																		send_to: 'hakan+test2345@talentics.app',
																		cc: offerTemplate.cc,
																		bcc: offerTemplate.bcc,
																	}"
																	:completedEmailForm="{
																		template: offerTemplate.email_body,
																		subject: offerTemplate.email_subject,
																	}"
																	:candidateEmails="[
																		'hakan+test2345@talentics.app',
																	]"
																	:previewMode="false"
																	@change-form="handleChangeScheduleEmailForm"
																	:onNewAttachmentAdded="
																		handleNewAttachmentAdded
																	"
																	:onAttachmentDeleteClick="
																		handleAttachmentDeleteClick
																	"
																	send-on-approval-flow-complete
																></ScheduleEmailForm>
															</div>
														</fieldset>
													</v-col>
												</v-row>
											</v-container>
										</div>
									</div>
								</ValidationObserver>
							</v-stepper-content>

							<v-stepper-content :step="2">
								<ValidationObserver
									ref="form_step_2"
									tag="form"
									@submit="handleClickNext"
									id="form_step_1"
								>
									<v-container v-if="suitableRequisitionApprovalFlowError">
										<v-row>
											<v-col>
												<v-alert type="error">
													{{ $t('err.no-suitable-approval-flow') }}
												</v-alert>
											</v-col>
										</v-row>
									</v-container>
									<v-container v-else>
										<v-row
											v-if="
												requisitionType === 'new_offer' ||
												suitableRequisitionApprovalFlowTemplates.length > 1
											"
										>
											<v-col>
												<ValidationProvider
													rules="required"
													v-slot="{ errors }"
												>
													<v-select
														v-model="requisitionApprovalFlowTemplateId"
														:items="suitableRequisitionApprovalFlowTemplates"
														item-text="label"
														item-value="id"
														dense
														outlined
														hide-details
														:label="$t('label.requisition-approval-flow')"
														@change="
															newVal =>
																handleGetRequisitionApprovalFlowTemplate({
																	id: newVal,
																})
														"
														:error-messages="errors"
													></v-select>
												</ValidationProvider>
											</v-col>
										</v-row>
										<v-row v-if="requisitionApprovalFlowTemplate.initialized">
											<v-col>
												<fieldset
													class="talentics-fieldset talentics-fieldset-paddings"
												>
													<legend>
														{{ $t('label.settings') }}
													</legend>
													<v-checkbox
														v-model="
															requisitionApprovalFlowTemplate.publish_job_on_completion
														"
														:label="$t('label.publish_job_on_completion')"
														dense
														hide-details
													></v-checkbox>
												</fieldset>
											</v-col>
										</v-row>
										<v-row v-if="requisitionApprovalFlowTemplate.initialized">
											<v-col>
												<ApprovalFlow
													v-model="requisitionApprovalFlowTemplate.json_schema"
													:userApprovalRequests="user_approval_requests"
													:onRefreshData="handleRefreshApprovalFlowData"
													:mode="userRole === 777 ? 'edit' : 'preview'"
												></ApprovalFlow>
											</v-col>
										</v-row>
									</v-container>
								</ValidationObserver>
							</v-stepper-content>

							<v-stepper-content :step="3">
								<div id="requisition_summary">
									<div v-if="requisitionType === 'new_job'">
										<v-container>
											<v-row>
												<v-col>
													<div>
														<fieldset
															class="talentics-fieldset talentics-fieldset-paddings"
														>
															<legend>
																{{ $t('label.title') }}
															</legend>
															<div v-html="job.name"></div>
														</fieldset>
													</div>

													<div style="margin-top: 2rem">
														<fieldset
															class="talentics-fieldset talentics-fieldset-paddings"
														>
															<legend>
																{{ $t('label.location') }}
															</legend>
															<div
																v-html="
																	(
																		locations.find(
																			({ id }) => id === job.location,
																		) || {}
																	).name
																"
															></div>
														</fieldset>
													</div>

													<div style="margin-top: 2rem">
														<fieldset
															class="talentics-fieldset talentics-fieldset-paddings"
														>
															<legend>
																{{ $t('label.department-team') }}
															</legend>
															<div
																v-html="
																	(
																		teams.find(
																			({ id }) => id === job.team_id,
																		) || {}
																	).name
																"
															></div>
														</fieldset>
													</div>

													<div style="margin-top: 2rem">
														<fieldset
															class="talentics-fieldset talentics-fieldset-paddings"
														>
															<legend>
																{{ $t('label.owner') }}
															</legend>
															<div
																v-html="
																	users.find(user => user.id === job.owner)
																		?.name
																"
															></div>
														</fieldset>
													</div>

													<div style="margin-top: 2rem">
														<fieldset
															class="talentics-fieldset talentics-fieldset-paddings"
														>
															<legend>
																{{ $t('label.hiring-manager') }}
															</legend>
															<div
																v-html="
																	users.find(
																		user => user.id === job.hiring_manager,
																	)?.name
																"
															></div>
														</fieldset>
													</div>

													<div style="margin-top: 2rem">
														<fieldset
															class="talentics-fieldset talentics-fieldset-paddings"
														>
															<legend>
																{{ $t('label.work-type') }}
															</legend>
															<div
																v-html="
																	(
																		workTypes.find(
																			({ id }) => id === job.work_type,
																		) || {}
																	)?.name
																"
															></div>
														</fieldset>
													</div>
												</v-col>
											</v-row>
										</v-container>

										<v-container>
											<v-row>
												<v-col>
													<DynamicForm
														:jsonSchema="requisitionFormTemplate.json_schema"
														:contextSchema="
															requisitionFormTemplate.context_schema
														"
														read-only
													></DynamicForm>
												</v-col>
											</v-row>
										</v-container>
									</div>

									<div v-else-if="requisitionType === 'new_offer'">
										<v-container>
											<v-row>
												<v-col>
													<fieldset
														class="talentics-fieldset talentics-fieldset-paddings"
													>
														<legend>
															{{ $t('label.offer-letter') }}
														</legend>
														<div
															v-html="sanitize(offerTemplate.offer_template)"
														></div>
													</fieldset>
												</v-col>
											</v-row>
										</v-container>
									</div>

									<v-container>
										<v-row>
											<v-col>
												<fieldset
													class="talentics-fieldset talentics-fieldset-paddings"
												>
													<legend>
														{{ $t('label.publish_job_on_completion') }}
													</legend>
													<div
														v-html="
															requisitionApprovalFlowTemplate.publish_job_on_completion
																? $t('text.yes')
																: $t('text.no')
														"
													></div>
												</fieldset>
											</v-col>
										</v-row>
									</v-container>
									<v-container>
										<v-row>
											<v-col>
												<ApprovalFlow
													v-model="requisitionApprovalFlowTemplate.json_schema"
													:userApprovalRequests="user_approval_requests"
													:onRefreshData="handleRefreshApprovalFlowData"
													mode="preview"
												></ApprovalFlow>
											</v-col>
										</v-row>
									</v-container>
								</div>
							</v-stepper-content>
						</v-stepper-items>
					</v-stepper>
				</v-card-text>

				<v-divider></v-divider>

				<v-card-actions>
					<v-btn @click="handleClickPrint" v-if="stepper === 3">
						<v-icon left>print</v-icon>
						{{ $t('button.print') }}
					</v-btn>
					<v-spacer></v-spacer>
					<v-btn @click="onClose" text>
						{{ $t('button.close') }}
					</v-btn>
					<v-btn
						color="secondary"
						@click="() => handleClickSubmit({ status: 'draft' })"
						v-if="status !== 'published' && (stepper === 2 || stepper === 3)"
					>
						<v-icon left>note_alt</v-icon>
						{{ $t('button.save-as-draft') }}
					</v-btn>
					<v-btn
						@click="handleClickBack"
						v-if="status !== 'published' && stepper > 1"
					>
						{{ $t('button.back') }}
					</v-btn>
					<v-btn
						color="primary"
						@click="handleClickNext"
						v-if="status !== 'published' && (stepper === 1 || stepper === 2)"
						:disabled="
							(stepper === 2 && suitableRequisitionApprovalFlowError) ||
							(stepper === 2 && approvalFlowTemplateLoading)
						"
						:loading="stepper === 2 && approvalFlowTemplateLoading"
					>
						{{ $t('button.next') }}
					</v-btn>
					<v-btn
						color="primary"
						@click="() => handleClickSubmit({ status: 'published' })"
						v-if="status !== 'published' && stepper === 3"
					>
						<v-icon left>task</v-icon>
						{{ $t('button.start-approval-flow') }}
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</div>
</template>

<script>
import { format } from 'date-fns';
import { mapState } from 'vuex';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import DynamicForm from '@/components/shared/dynamicForm/DynamicForm';
import ApprovalFlow from '@/components/shared/approvalFlow/ApprovalFlow';
import RichEditor from '@/components/shared/richEditor/RichEditor';
import ScheduleEmailForm from '@/components/shared/scheduleEmailForm/ScheduleEmailForm';
import PositionPicker from '@/components/shared/positionPicker/PositionPicker';
import api from '@/api';
import UserPicker from '@/components/shared/userPicker/UserPicker';
import DatePicker from '@/components/shared/datePicker/DatePicker';
import { sanitize } from '@/utils/sanitize';

export default {
	computed: {
		...mapState({
			users: state => state.users.items,
			isDark: state => state.currentUser.isDark,
			requisitionFormTemplates: state =>
				state.requisitionFormTemplates.items.filter(
					({ requisition_type }) => requisition_type === 'new_job',
				),
			requisitionApprovalFlowTemplates: state =>
				state.requisitionApprovalFlowTemplates.items,
			locations: state => state.locations.items,
			workTypes: state => state.workTypes.items,
			currentLang: state => state.currentUser.lang,
			teams: state => state.teams.items,
			offerTemplates: state => state.offerTemplates.items,
			userRole: state => state.currentUser.userRole,
		}),
	},
	components: {
		ValidationObserver,
		ValidationProvider,
		DynamicForm,
		ApprovalFlow,
		RichEditor,
		ScheduleEmailForm,
		PositionPicker,
		UserPicker,
		DatePicker,
	},
	props: {
		onClose: {
			type: Function,
			required: false,
			default() {
				return false;
			},
		},
		requisitionType: {
			type: String,
			required: true,
		},
		actionType: {
			type: String,
			required: false,
			default() {
				return 'new';
			},
		},
		requisitionId: {
			type: [Number, String],
			required: false,
			default() {
				return null;
			},
		},
	},
	data() {
		return {
			format,
			isVisible: false,
			stepper: 1,
			requisitionFormTemplateId: null,
			requisitionFormTemplate: {
				json_schema: [],
				context_schema: {},
				initialized: false,
				job_type: 'new_position',
				number_of_people: 1,
				deadline: null,
				predefined_position_id: null,
				predefined_position_preset_id: null,
			},
			requisitionApprovalFlowTemplateId: null,
			requisitionApprovalFlowTemplate: {
				json_schema: [],
				publish_job_on_completion: null,
				initialized: false,
				requisition_approval_flow_template_id: null,
			},
			job: {
				id: null,
				name: '',
				location: null,
				department_id: null,
				team_id: null,
				work_type: null,
				hiring_manager: null,
				owner: null,
				description: null,
				closing: null,
				internal_notes: null,
				list_on_external: false,
				access_users: [],
			},
			suitableRequisitionApprovalFlowTemplates: [],
			suitableRequisitionApprovalFlowError: false,
			status: null,
			requested_by: null,
			requisition_created: null,
			user_approval_requests: [],
			offerTemplateId: null,
			offerTemplate: {
				initialized: false,
				email_subject: null,
				email_body: null,
				offer_template: null,
				send_automated_email: false,
				sent_from: null,
				cc: [],
				bcc: [],
				attachments: [],
			},
			similarPositionRequisitions: [],
			similarPositionRequisitionsRadioGroup: 0,
			approvalFlowTemplateLoading: false,
		};
	},
	methods: {
		sanitize,
		handleClickOutside(evt) {
			evt.preventDefault();
		},
		handleGetRequisitionFormTemplate({ id }) {
			this.requisitionFormTemplate = {
				...this.requisitionFormTemplate,
				initialized: true,
			};
			this.$store
				.dispatch('requisitionFormTemplates/get', { id, refresh: true })
				.then(response => {
					const { items } = response.data;
					if (items.length > 0) {
						this.requisitionFormTemplate = {
							...this.requisitionFormTemplate,
							json_schema: [...items[0].created_json_schema],
						};
					}
				});
		},
		handleGetOfferTemplate({ id }) {
			this.$store
				.dispatch('offerTemplates/get', { id, refresh: true })
				.then(response => {
					const { items } = response.data;
					if (items.length > 0) {
						this.offerTemplate = {
							...items[0],
						};
					}
				});
		},
		handleGetRequisitionApprovalFlowTemplate({ id }) {
			this.approvalFlowTemplateLoading = true;
			this.requisitionApprovalFlowTemplate = {
				...this.requisitionApprovalFlowTemplate,
				initialized: true,
			};
			this.$store
				.dispatch('requisitionApprovalFlowTemplates/get', {
					id,
					refresh: true,
				})
				.then(response => {
					const { items } = response.data;
					if (items.length > 0) {
						this.requisitionApprovalFlowTemplate = {
							...this.requisitionApprovalFlowTemplate,
							json_schema: [...items[0].json_schema],
							publish_job_on_completion: items[0].publish_job_on_completion,
						};
					}
				})
				.finally(() => {
					this.approvalFlowTemplateLoading = false;
				});
		},
		handleDynamicFormChange({ newVal }) {
			this.requisitionFormTemplate = {
				...this.requisitionFormTemplate,
				context_schema: {
					...this.requisitionFormTemplate.context_schema,
					...newVal,
				},
			};
		},
		async handleClickNext(evt) {
			if (evt) {
				evt.preventDefault();
			}

			if (this.stepper === 1) {
				const isValid = await this.$refs.form_step_1.validate();

				if (isValid) {
					this.stepper += 1;
				}
			} else if (this.stepper === 2) {
				const isValid = await this.$refs.form_step_2.validate();

				if (isValid) {
					this.stepper += 1;
				}
			}
		},
		handleClickBack() {
			this.stepper -= 1;
		},
		handlePositionPickerChange({
			department_id,
			team_id,
			predefined_position_id,
			predefined_position_preset_id,
			name,
			team_id_changed = false,
		}) {
			if (team_id_changed && team_id) {
				this.handlePickingApprovalFlow();
			}
			this.similarPositionRequisitionsRadioGroup = 0;

			if (
				predefined_position_preset_id &&
				predefined_position_preset_id !==
					this.requisitionFormTemplate.predefined_position_preset_id
			) {
				this.handleGetSimilarPositionRequisitions({
					predefined_position_preset_id,
				});
			} else {
				this.similarPositionRequisitions = [];
			}
			this.job = {
				...this.job,
				department_id,
				team_id,
				name,
			};

			this.requisitionFormTemplate = {
				...this.requisitionFormTemplate,
				predefined_position_id,
				predefined_position_preset_id,
			};
		},
		handlePickingApprovalFlow() {
			const { team_id } = this.job;
			const { job_type } = this.requisitionFormTemplate;
			const { requisitionApprovalFlowTemplates, requisitionType } = this;
			if (requisitionType === 'new_job') {
				const suitableRequisitionApprovalFlowTemplates =
					requisitionApprovalFlowTemplates.filter(
						({
							team_ids,
							is_valid_job_new_position,
							is_valid_job_replacement,
						}) => {
							if (job_type === 'new_position') {
								return is_valid_job_new_position && team_ids.includes(team_id);
							} else if (job_type === 'replacement') {
								return is_valid_job_replacement && team_ids.includes(team_id);
							}
							return team_ids.includes(team_id);
						},
					);

				if (suitableRequisitionApprovalFlowTemplates?.length > 0) {
					this.suitableRequisitionApprovalFlowTemplates =
						suitableRequisitionApprovalFlowTemplates;
					this.handleGetRequisitionApprovalFlowTemplate({
						id:
							this.requisitionApprovalFlowTemplateId ||
							suitableRequisitionApprovalFlowTemplates[0].id,
					});
					this.suitableRequisitionApprovalFlowError = false;
				} else {
					this.suitableRequisitionApprovalFlowTemplates = [];
					this.requisitionApprovalFlowTemplateId = null;
					this.requisitionApprovalFlowTemplate = {
						...this.requisitionApprovalFlowTemplate,
						json_schema: [],
						publish_job_on_completion: false,
					};
					this.suitableRequisitionApprovalFlowError = true;
				}
			} else if (requisitionType === 'new_offer') {
				const suitableRequisitionApprovalFlowTemplates =
					requisitionApprovalFlowTemplates;
				this.suitableRequisitionApprovalFlowTemplates =
					suitableRequisitionApprovalFlowTemplates;
			}
		},
		handleGetSimilarPositionRequisitions({ predefined_position_preset_id }) {
			api.requisitions.similar
				.get({ predefined_position_preset_id })
				.then(response => {
					const { items } = response.data;
					this.similarPositionRequisitions = items;
				});
		},
		handleGetJobAccessUsers() {
			const { owner, hiring_manager, access_users } = this.job;
			const { requisitionApprovalFlowTemplate } = this;

			const potential_access_users = [
				...requisitionApprovalFlowTemplate.json_schema.reduce(
					(acc, { approvers }) => {
						return [
							...acc,
							...(approvers ? approvers.map(({ user_id }) => user_id) : []),
						];
					},
					[...access_users, owner, hiring_manager],
				),
			];

			return [...new Set(potential_access_users)];
		},
		async handleClickSubmit({ status }) {
			const { requisitionType } = this;

			if (requisitionType === 'new_offer') {
				api.offers.post();
				return false;
			}

			let job_id = null;
			let requisition_id = this.requisitionId
				? parseInt(this.requisitionId, 10)
				: null;

			// FIRST CHECK IF JOB IS ALREADY CREATED
			// IF JOB ID IS NOT NULL, THEN UPDATE JOB
			if (this.job.id) {
				const jobItems = [
					{
						...this.job,
						access_users: this.handleGetJobAccessUsers(),
					},
				];

				await this.$store.dispatch('jobs/put', { items: jobItems });
				job_id = this.job.id;
			}
			// ELSE CREATE NEW JOB
			else {
				const jobItems = [
					{
						...this.job,
						status: 'pending',
						access_users: this.handleGetJobAccessUsers(),
					},
				];

				const jobRequest = await this.$store.dispatch('jobs/post', {
					items: jobItems,
				});

				if (jobRequest.data.items.length > 0) {
					job_id = jobRequest.data.items[0].id;
				}
			}

			const { actionType, requisitionApprovalFlowTemplate } = this;

			// CREATE NEW REQUISITION
			if (actionType === 'new') {
				const {
					json_schema,
					context_schema,
					job_type,
					number_of_people,
					deadline,
					predefined_position_id,
					predefined_position_preset_id,
				} = this.requisitionFormTemplate;

				const requisitionRequest = await api.requisitions.post({
					items: [
						{
							requisition_type: 'new_job',
							requisition_form_json_schema: json_schema,
							requisition_form_context_schema: context_schema,
							approval_flow_json_schema:
								requisitionApprovalFlowTemplate.json_schema,
							job_type: job_type,
							number_of_people: number_of_people,
							deadline: deadline || null,
							status,
							job_id,
							active: true,
							predefined_position_id: predefined_position_id,
							predefined_position_preset_id: predefined_position_preset_id,
							publish_job_on_completion:
								this.requisitionApprovalFlowTemplate.publish_job_on_completion,
							requisition_approval_flow_template_id:
								this.requisitionApprovalFlowTemplateId || null,
						},
					],
				});

				if (requisitionRequest.data.items.length > 0) {
					requisition_id = requisitionRequest.data.items[0].id;
				}
			}
			// EDIT EXISTING REQUISITION
			else if (actionType === 'edit') {
				const {
					json_schema,
					context_schema,
					job_type,
					number_of_people,
					deadline,
					predefined_position_id,
					predefined_position_preset_id,
				} = this.requisitionFormTemplate;

				await api.requisitions.put({
					items: [
						{
							id: requisition_id,
							requisition_form_json_schema: json_schema,
							requisition_form_context_schema: context_schema,
							approval_flow_json_schema:
								requisitionApprovalFlowTemplate.json_schema,
							job_type: job_type,
							number_of_people: number_of_people,
							deadline: deadline || null,
							status,
							active: true,
							predefined_position_id,
							predefined_position_preset_id,
							publish_job_on_completion:
								requisitionApprovalFlowTemplate.publish_job_on_completion,
							requisition_approval_flow_template_id:
								this.requisitionApprovalFlowTemplateId,
						},
					],
				});
			}

			if (status === 'published') {
				// IF STATUS IS PUBLISHED, THEN CREATE REQUIRED USER APPROVAL REQUESTS
				// AND UPDATE REQUISITION APPROVAL FLOW JSON SCHEMA
				this.requisitionApprovalFlowTemplate = {
					...this.requisitionApprovalFlowTemplate,
					json_schema: await Promise.all(
						this.requisitionApprovalFlowTemplate.json_schema.map(async step => {
							return {
								approvers: await Promise.all(
									step.approvers.map(async approver => {
										const { user_id } = approver;
										const approvalRequestRequest =
											await api.userApprovalRequests.post({
												items: [
													{
														job_id,
														requested_from: user_id,
														status: 'queued',
														requisition_type: this.requisitionType,
														requisition_id,
													},
												],
											});
										return {
											...approver,
											approval_request_id:
												approvalRequestRequest.data.items[0][0].id,
										};
									}),
								),
							};
						}),
					),
				};

				// UPDATE REQUISITION APPROVAL FLOW JSON SCHEMA
				await api.requisitions.put({
					items: [
						{
							id: requisition_id,
							approval_flow_json_schema:
								this.requisitionApprovalFlowTemplate.json_schema,
						},
					],
					set_approval_request_statuses: true,
					requisition_id,
				});
			}

			// FINALLY REDIRECT USER TO RELATED JOB PAGE
			this.$store.commit('jobs/setItemNeedsToRefresh');
			const { currentLang } = this;
			this.$router.push({
				path: `/${currentLang}/jobs/edit/${job_id}`,
				query: {
					status: 'pending',
				},
			});
		},
		handleGetRequisition({ id, fillFromSimilarRequisition = false }) {
			api.requisitions.get({ id }).then(response => {
				const { items } = response.data;

				if (items.length > 0) {
					const {
						requisition_form_json_schema,
						requisition_form_context_schema,
						approval_flow_json_schema,
						status,
						job_id,
						user_approval_requests,
						number_of_people,
						deadline,
						created,
						job_type,
						predefined_position_id,
						predefined_position_preset_id,
						publish_job_on_completion,
						requisition_approval_flow_template_id,
						requested_by,
					} = items[0];

					if (fillFromSimilarRequisition === false) {
						this.status = status;
						this.requested_by = requested_by;
						this.requisition_created = created || null;
						this.user_approval_requests = user_approval_requests;

						this.requisitionFormTemplate = {
							...this.requisitionFormTemplate,
							json_schema: [...requisition_form_json_schema],
							context_schema: {
								...requisition_form_context_schema,
							},
							number_of_people,
							deadline: deadline || null,
							job_type,
							predefined_position_id,
							predefined_position_preset_id,
							initialized: true,
						};

						this.requisitionApprovalFlowTemplate = {
							json_schema: [...approval_flow_json_schema],
							publish_job_on_completion,
							requisition_approval_flow_template_id,
							initialized: true,
						};

						this.handleGetJob({ id: job_id, preserveId: true });

						if (status === 'published') {
							this.stepper = 3;
						}
					} else {
						this.requisitionFormTemplate = {
							...this.requisitionFormTemplate,
							json_schema: [...requisition_form_json_schema],
							context_schema: {
								...requisition_form_context_schema,
							},
							number_of_people,
							deadline: deadline || null,
							job_type,
							predefined_position_id,
							predefined_position_preset_id,
							initialized: true,
						};

						this.requisitionApprovalFlowTemplate = {
							json_schema: [...approval_flow_json_schema],
							publish_job_on_completion,
							requisition_approval_flow_template_id,
							initialized: true,
						};

						this.handleGetJob({ id: job_id });
					}
				}
			});
		},
		handleGetJob({ id, preserveId = false }) {
			api.jobs.get({ id }).then(response => {
				const { items } = response.data;

				if (items.length > 0) {
					const { ...data } = items[0];
					this.job = {
						...data,
						id: preserveId ? data.id : null,
					};
				}
			});
		},
		handleRefreshApprovalFlowData() {
			const { requisitionId, requisitionType } = this;
			this.handleGetRequisition({ id: requisitionId });

			if (requisitionType === 'new_job') {
				this.$store.commit('jobs/setItemNeedsToRefresh');
			}
		},
		handleClickPrint() {
			const {
				job,
				users,
				locations,
				workTypes,
				teams,
				requisitionType,
				requisitionFormTemplate,
				requisitionApprovalFlowTemplate,
				user_approval_requests,
				status,
				currentLang,
				requisition_created,
				requested_by,
			} = this;
			const isEN = currentLang === 'en';

			const locationName =
				(locations.find(({ id }) => id === job.location) || {}).name || '-';
			const teamName =
				(teams.find(({ id }) => id === job.team_id) || {}).name || '-';
			const ownerName =
				(users.find(user => user.id === job.owner) || {}).name || '-';
			const hiringManagerName =
				(users.find(user => user.id === job.hiring_manager) || {}).name || '-';
			const workTypeName =
				(workTypes.find(({ id }) => id === job.work_type) || {}).name || '-';
			const requestedByName =
				(users.find(user => parseInt(user.id, 10) === parseInt(requested_by, 10)) || {}).name || '-';

			const printDate = this.format(new Date(), 'dd.MM.yyyy HH:mm');

			// Build approval flow status
			let approvalStatusLabel = '';
			let approvalStatusColor = '#666';
			if (user_approval_requests && user_approval_requests.length > 0) {
				const hasRejected = user_approval_requests.some(
					r => r.status === 'rejected',
				);
				const allApproved = user_approval_requests.every(
					r => r.status === 'approved',
				);
				const hasPending = user_approval_requests.some(
					r => r.status === 'pending',
				);
				if (hasRejected) {
					approvalStatusLabel = isEN ? 'Rejected' : 'Reddedildi';
					approvalStatusColor = '#D32F2F';
				} else if (allApproved) {
					approvalStatusLabel = isEN ? 'Approved' : 'Onaylandı';
					approvalStatusColor = '#388E3C';
				} else if (hasPending) {
					approvalStatusLabel = isEN ? 'In Progress' : 'Devam Ediyor';
					approvalStatusColor = '#F57C00';
				} else {
					approvalStatusLabel = isEN ? 'Queued' : 'Sırada';
					approvalStatusColor = '#666';
				}
			}

			// Build approval steps — check if ANY step across the entire flow has a rejection reason
			const approvalSchema = requisitionApprovalFlowTemplate.json_schema || [];
			const hasAnyRejection = user_approval_requests.some(r => r.rejection_reason);

			let approvalStepsHtml = '';
			approvalSchema.forEach((step, stepIndex) => {
				const stepApprovers = (step.approvers || []).map(approver => {
					const user = users.find(
						u => parseInt(u.id, 10) === parseInt(approver.user_id, 10),
					);
					const approvalRequest = user_approval_requests.find(
						r => r.id === approver.approval_request_id,
					);
					const name = user
						? user.name || user.email
						: isEN
						? 'Unknown'
						: 'Bilinmiyor';
					let statusText = '-';
					let statusColor = '#666';
					let dateText = '';
					let rejectionReason = '';

					if (approvalRequest) {
						if (approvalRequest.status === 'approved') {
							const isOverridden =
								approvalRequest.approved_by &&
								approvalRequest.requested_from &&
								parseInt(approvalRequest.approved_by, 10) !==
									parseInt(approvalRequest.requested_from, 10);
							if (isOverridden) {
								const adminUser = users.find(
									u => parseInt(u.id, 10) === parseInt(approvalRequest.approved_by, 10),
								);
								const adminName = adminUser ? (adminUser.name || adminUser.email) : '';
								statusText = adminName
									? (isEN ? `Overridden by ${adminName}` : `${adminName} tarafından onaylandı`)
									: (isEN ? 'Overridden' : 'Yönetici Onayı');
							} else {
								statusText = isEN ? 'Approved' : 'Onaylandı';
							}
							statusColor = isOverridden ? '#42A5F5' : '#388E3C';
							dateText = approvalRequest.updated
								? this.format(
										new Date(approvalRequest.updated),
										'dd.MM.yyyy HH:mm',
								  )
								: '';
						} else if (approvalRequest.status === 'rejected') {
							statusText = isEN ? 'Rejected' : 'Reddedildi';
							statusColor = '#D32F2F';
							dateText = approvalRequest.updated
								? this.format(
										new Date(approvalRequest.updated),
										'dd.MM.yyyy HH:mm',
								  )
								: '';
							rejectionReason = approvalRequest.rejection_reason || '';
						} else if (approvalRequest.status === 'pending') {
							statusText = isEN ? 'Pending' : 'Bekliyor';
							statusColor = '#F57C00';
						}
					}

					return { name, statusText, statusColor, dateText, rejectionReason };
				});

				approvalStepsHtml += `
					<div style="margin-bottom: 12px;">
						<div style="font-weight: 500; font-size: 13px; color: #555; margin-bottom: 6px; letter-spacing: 0.5px;">
							${isEN ? 'Step' : 'Adım'} ${stepIndex + 1}
						</div>
						<table style="width: 100%; table-layout: fixed; border-collapse: collapse; font-size: 13px;">
							<thead>
								<tr style="background: #f5f5f5;">
									<th style="text-align: left; padding: 6px 10px; border-bottom: 1px solid #e0e0e0; width: ${hasAnyRejection ? '30%' : '40%'};">${
										isEN ? 'Approver' : 'Onaylayan'
									}</th>
									<th style="text-align: left; padding: 6px 10px; border-bottom: 1px solid #e0e0e0; width: ${hasAnyRejection ? '20%' : '30%'};">${
										isEN ? 'Status' : 'Durum'
									}</th>
									<th style="text-align: left; padding: 6px 10px; border-bottom: 1px solid #e0e0e0; width: ${hasAnyRejection ? '20%' : '30%'};">${
										isEN ? 'Date' : 'Tarih'
									}</th>
									${
										hasAnyRejection
											? `<th style="text-align: left; padding: 6px 10px; border-bottom: 1px solid #e0e0e0; width: 30%;">${
													isEN ? 'Reason' : 'Neden'
											  }</th>`
											: ''
									}
								</tr>
							</thead>
							<tbody>
								${stepApprovers
									.map(
										a => `
									<tr>
										<td style="padding: 6px 10px; border-bottom: 1px solid #f0f0f0;">${a.name}</td>
										<td style="padding: 6px 10px; border-bottom: 1px solid #f0f0f0;"><span style="color: ${
											a.statusColor
										}; font-weight: 500;">${a.statusText}</span></td>
										<td style="padding: 6px 10px; border-bottom: 1px solid #f0f0f0;">${
											a.dateText
										}</td>
										${
											hasAnyRejection
												? `<td style="padding: 6px 10px; border-bottom: 1px solid #f0f0f0;">${a.rejectionReason}</td>`
												: ''
										}
									</tr>
								`,
									)
									.join('')}
							</tbody>
						</table>
					</div>
				`;
			});

			// Build form fields from schema data (skip empty fields, skip file type)
			const jsonSchema = requisitionFormTemplate.json_schema || [];
			const contextSchema = requisitionFormTemplate.context_schema || {};
			const formFieldRows = [];

			jsonSchema.forEach(field => {
				const rawValue = contextSchema[field.paramName];
				if (rawValue === undefined || rawValue === null || rawValue === '') return;
				if (field.type === 'file') return;

				let displayValue = '';

				if (field.type === 'radio' || field.type === 'select') {
					const option = (field.options || []).find(
						o => o.value === rawValue || String(o.value) === String(rawValue),
					);
					displayValue = option ? option.label : String(rawValue);
				} else if (field.type === 'switch') {
					displayValue = rawValue ? (isEN ? 'Yes' : 'Evet') : (isEN ? 'No' : 'Hayır');
				} else if (field.type === 'datePicker') {
					try {
						displayValue = this.format(new Date(rawValue), 'dd.MM.yyyy');
					} catch (e) {
						displayValue = String(rawValue);
					}
				} else if (field.type === 'userPicker') {
					if (field.multiple) {
						const userIds = Array.isArray(rawValue) ? rawValue : [rawValue];
						displayValue = userIds
							.map(uid => {
								const u = users.find(user => parseInt(user.id, 10) === parseInt(uid, 10));
								return u ? (u.name || u.email) : String(uid);
							})
							.join(', ');
					} else {
						const u = users.find(user => parseInt(user.id, 10) === parseInt(rawValue, 10));
						displayValue = u ? (u.name || u.email) : String(rawValue);
					}
				} else if (field.type === 'textarea') {
					displayValue = String(rawValue).replace(/\n/g, '<br>');
				} else {
					displayValue = String(rawValue);
				}

				if (displayValue) {
					formFieldRows.push({ label: field.label, value: displayValue });
				}
			});

			let formFieldsHtml = '';
			if (formFieldRows.length > 0) {
				formFieldsHtml = `
					<table style="width: 100%; border-collapse: collapse; font-size: 13px;">
						${formFieldRows
							.map(
								f => `
							<tr>
								<td style="padding: 8px 10px; border-bottom: 1px solid #f0f0f0; color: #666; width: 220px; font-weight: 500; vertical-align: top;">${f.label}</td>
								<td style="padding: 8px 10px; border-bottom: 1px solid #f0f0f0;">${f.value}</td>
							</tr>
						`,
							)
							.join('')}
					</table>
				`;
			}

			// Build the job info section
			let jobInfoHtml = '';
			if (requisitionType === 'new_job') {
				const fields = [
					{ label: isEN ? 'Title' : 'Başlık', value: job.name },
					{ label: isEN ? 'Location' : 'Lokasyon', value: locationName },
					{
						label: isEN ? 'Department / Team' : 'Departman / Takım',
						value: teamName,
					},
					{ label: isEN ? 'Job Owner' : 'İş İlanı Sahibi', value: ownerName },
					{
						label: isEN ? 'Hiring Manager' : 'İşe Alım Müdürü',
						value: hiringManagerName,
					},
					{ label: isEN ? 'Work Type' : 'Çalışma Şekli', value: workTypeName },
					{
						label: isEN ? 'Requested By' : 'Talep Gönderen',
						value: requestedByName,
					},
				];

				jobInfoHtml = `
					<table style="width: 100%; border-collapse: collapse; font-size: 13px;">
						${fields
							.map(
								f => `
							<tr>
								<td style="padding: 8px 10px; border-bottom: 1px solid #f0f0f0; color: #666; width: 220px; font-weight: 500;">${
									f.label
								}</td>
								<td style="padding: 8px 10px; border-bottom: 1px solid #f0f0f0;">${
									f.value || '-'
								}</td>
							</tr>
						`,
							)
							.join('')}
					</table>
				`;
			}

			// Build requisition meta info (created, number_of_people, deadline)
			const requisitionMetaFields = [];
			if (requisition_created) {
				requisitionMetaFields.push({
					label: isEN ? 'Requisition Create Date' : 'Talep Oluşturma Tarihi',
					value: this.format(new Date(requisition_created), 'dd.MM.yyyy HH:mm'),
				});
			}
			if (requisitionFormTemplate.number_of_people) {
				requisitionMetaFields.push({
					label: isEN ? 'Targeted Number of Employees To Be Hired' : 'Hedeflenen İşe Alınacak Kişi Sayısı',
					value: String(requisitionFormTemplate.number_of_people),
				});
			}
			if (requisitionFormTemplate.deadline) {
				requisitionMetaFields.push({
					label: isEN ? 'Targeted Requisition Close Date' : 'Hedeflenen Talep Kapama Tarihi',
					value: this.format(new Date(requisitionFormTemplate.deadline), 'dd.MM.yyyy'),
				});
			}

			let requisitionMetaHtml = '';
			if (requisitionMetaFields.length > 0) {
				requisitionMetaHtml = `
					<div class="section">
						<div class="section-title">${isEN ? 'Requisition Info' : 'Talep Bilgileri'}</div>
						<table style="width: 100%; border-collapse: collapse; font-size: 13px;">
							${requisitionMetaFields
								.map(
									f => `
								<tr>
									<td style="padding: 8px 10px; border-bottom: 1px solid #f0f0f0; color: #666; width: 220px; font-weight: 500;">${f.label}</td>
									<td style="padding: 8px 10px; border-bottom: 1px solid #f0f0f0;">${f.value}</td>
								</tr>
							`,
								)
								.join('')}
						</table>
					</div>
				`;
			}

			const publishOnCompletion =
				requisitionApprovalFlowTemplate.publish_job_on_completion;

			var mywindow = window.open('', 'PRINT', 'height=600,width=800');

			mywindow.document.write(`
				<html>
				<head>
					<title>${isEN ? 'Requisition Summary' : 'Talep Özeti'}</title>
					<link rel="stylesheet" href="//fonts.googleapis.com/css?family=Roboto:400,500,700,400italic">
					<meta name="viewport" content="width=device-width, initial-scale=1">
					<style>
						* {
							font-family: 'Roboto', sans-serif;
							margin: 0;
							padding: 0;
							box-sizing: border-box;
						}
						body {
							padding: 32px;
							color: #333;
							line-height: 1.5;
							max-width: 800px;
							margin: 0 auto;
						}
						.print-header {
							display: flex;
							justify-content: space-between;
							align-items: flex-start;
							border-bottom: 2px solid #1976D2;
							padding-bottom: 16px;
							margin-bottom: 24px;
						}
						.print-header h1 {
							font-size: 20px;
							font-weight: 500;
							color: #1976D2;
							margin: 0;
						}
						.print-header .subtitle {
							font-size: 13px;
							color: #666;
							margin-top: 4px;
						}
						.print-header .meta {
							text-align: right;
							font-size: 12px;
							color: #888;
						}
						.overall-status {
							display: inline-block;
							padding: 4px 12px;
							border-radius: 4px;
							font-size: 13px;
							font-weight: 500;
							color: white;
							margin-top: 6px;
						}
						.section {
							margin-bottom: 24px;
						}
						.section-title {
							font-size: 14px;
							font-weight: 500;
							color: #1976D2;
							letter-spacing: 0.5px;
							margin-bottom: 10px;
							padding-bottom: 4px;
							border-bottom: 1px solid #e0e0e0;
						}
						.publish-flag {
							display: inline-block;
							padding: 3px 10px;
							border-radius: 4px;
							font-size: 13px;
							font-weight: 500;
						}
						@media print {
							body { padding: 16px; }
							.section { page-break-inside: avoid; }
						}
					</style>
				</head>
				<body>
					<div class="print-header">
						<div>
							<h1>${isEN ? 'Requisition Summary' : 'Talep Özeti'}</h1>
							<div class="subtitle">${job.name || ''}</div>
							${
								approvalStatusLabel
									? `<div class="overall-status" style="background: ${approvalStatusColor};">${approvalStatusLabel}</div>`
									: ''
							}
						</div>
						<div class="meta">
							<div>${printDate}</div>
							${
								status
									? `<div style="margin-top: 2px; text-transform: capitalize;">${
											isEN ? 'Status' : 'Durum'
									  }: ${status}</div>`
									: ''
							}
						</div>
					</div>

					${
						requisitionType === 'new_job'
							? `
						<div class="section">
							<div class="section-title">${isEN ? 'Job Information' : 'İlan Bilgileri'}</div>
							${jobInfoHtml}
						</div>
					`
							: ''
					}

					${requisitionMetaHtml}

					${
						formFieldsHtml
							? `
						<div class="section">
							<div class="section-title">${
								isEN ? 'Requisition Details' : 'Talep Detayları'
							}</div>
							${formFieldsHtml}
						</div>
					`
							: ''
					}

					${
						approvalSchema.length > 0
							? `
						<div class="section">
							<div class="section-title">${isEN ? 'Approval Flow' : 'Onay Akışı'}</div>
							${approvalStepsHtml}
						</div>
					`
							: ''
					}

					<div class="section">
						<div class="section-title">${isEN ? 'Settings' : 'Ayarlar'}</div>
						<table style="width: 100%; border-collapse: collapse; font-size: 13px;">
							<tr>
								<td style="padding: 8px 10px; border-bottom: 1px solid #f0f0f0; color: #666; width: 250px; font-weight: 500;">${
									isEN
										? 'Publish Job on Completion'
										: 'Onay Tamamlanınca İlanı Yayınla'
								}</td>
								<td style="padding: 8px 10px; border-bottom: 1px solid #f0f0f0;">
									<span class="publish-flag" style="background: ${
										publishOnCompletion
											? '#E8F5E9; color: #388E3C'
											: '#FBE9E7; color: #D32F2F'
									};">
										${publishOnCompletion ? (isEN ? 'Yes' : 'Evet') : isEN ? 'No' : 'Hayır'}
									</span>
								</td>
							</tr>
						</table>
					</div>
				</body>
				</html>
			`);

			mywindow.document.close();
			mywindow.focus();

			return true;
		},
		handleChangeScheduleEmailForm(newVal) {
			console.log({ newVal });
		},
		async handleNewAttachmentAdded({ newFiles }) {
			console.log({ newFiles });
		},
		handleAttachmentDeleteClick({ index }) {
			console.log({ index });
		},
		handleGetUserName({ userId }) {
			const { users } = this;
			const user = users.find(item => item.id === userId);
			if (user) {
				const { name, email } = user;
				if (name) {
					return name;
				}
				return email;
			}
			return '-';
		},
		formatDate({ timestamp = new Date(), format = 'dd.MM.yyyy HH:mm:ss' }) {
			const dateToFormat = new Date(timestamp);
			return this.format(dateToFormat, format);
		},
		handleSimilarPositionRequisitionsRadioGroupChange(id) {
			if (id === 0) {
				this.handleEmptyRequisitionForm();
			} else {
				this.handleGetRequisition({
					id,
					fillFromSimilarRequisition: true,
				});
			}
		},
		handleEmptyRequisitionForm() {
			this.requisition_created = null;
			this.requisitionFormTemplateId = null;
			this.requisitionFormTemplate = {
				json_schema: [],
				context_schema: {},
				initialized: false,
				job_type: 'new_position',
				number_of_people: 1,
				deadline: null,
				predefined_position_id: null,
				predefined_position_preset_id: null,
			};
			this.requisitionApprovalFlowTemplateId = null;
			this.requisitionApprovalFlowTemplate = {
				json_schema: [],
				initialized: false,
			};

			const { requisitionFormTemplates, actionType } = this;

			if (requisitionFormTemplates.length > 0 && actionType === 'new') {
				const { id } = requisitionFormTemplates[0];
				this.requisitionFormTemplateId = id;
				this.handleGetRequisitionFormTemplate({ id });
			}
		},
	},
	created() {
		this.$store.dispatch('requisitionFormTemplates/get', {
			updateStore: true,
			refresh: true,
		});
		this.$store.dispatch('offerTemplates/get', {
			updateStore: true,
			refresh: true,
		});
		this.$store.dispatch('requisitionApprovalFlowTemplates/get', {
			updateStore: true,
		});
	},
	mounted() {
		setTimeout(
			function () {
				this.isVisible = true;
			}.bind(this),
			250,
		);
		const { actionType, requisitionId } = this;

		if (actionType === 'edit') {
			this.handleGetRequisition({ id: requisitionId });
		}
	},
	watch: {
		stepper: {
			handler: function (newVal, oldVal) {
				if (newVal && newVal !== oldVal) {
					if (newVal === 1) {
						const { requisitionFormTemplates, actionType } = this;
						const { initialized } = this.requisitionFormTemplate;

						if (
							requisitionFormTemplates.length > 0 &&
							actionType === 'new' &&
							initialized === false
						) {
							const { id } = requisitionFormTemplates[0];
							this.requisitionFormTemplateId = id;
							this.handleGetRequisitionFormTemplate({ id });
						}
					} else if (newVal === 2) {
						this.handlePickingApprovalFlow();
					}
				}
			},
			immediate: true,
			deep: true,
		},
		requisitionFormTemplates: {
			handler: function (newVal) {
				if (newVal && newVal.length > 0) {
					const { actionType } = this;
					const { initialized } = this.requisitionFormTemplate;

					if (actionType === 'new' && initialized === false) {
						const { id } = newVal[0];
						this.requisitionFormTemplateId = id;
						this.handleGetRequisitionFormTemplate({ id });
					}
				}
			},
			deep: true,
			immediate: true,
		},
		requisitionApprovalFlowTemplate: {
			handler: function (newVal) {
				if (
					!this.requisitionApprovalFlowTemplateId &&
					newVal.requisition_approval_flow_template_id
				) {
					this.requisitionApprovalFlowTemplateId =
						newVal.requisition_approval_flow_template_id;
				}

				if (
					!this.requisitionApprovalFlowTemplateId &&
					!newVal.requisition_approval_flow_template_id
				) {
					this.requisitionApprovalFlowTemplateId =
						this.suitableRequisitionApprovalFlowTemplates[0]?.id;
				}
			},
			deep: true,
			immediate: true,
		},
	},
};
</script>
